import Form from "react-bootstrap/Form";
import "./addapprovalworkflow.scss";
import { Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useApprovalWorkflow from "../../../Hooks/useApprovalWorkflow";
import { useAppSelector } from "../../../redux/hooks";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";
import Loading from "../../../components/LoadingPage/Loading";
import {
  PermissionIdentifier,
  PermissionModule,
  useModulePermissions,
} from "../../../utils/permissionUtils";
import { ApprovalMode } from "../../../redux/approvalWorkflow/approvalWorkflowSlice";

const AddApprovalWorkflow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {
    createApprovalWorkflowAPI,
    createApprovalWorkflowLoading,
    editApprovalWorkflowAPI,
    editApprovalWorkflowLoading,
    getApprovalWorkflowByIdAPI,
    getApprovalWorkflowByIdLoading,
    getApprovalModesAPI,
    getApprovalModesLoading,
  } = useApprovalWorkflow();

  const [isEdit, setIsEdit] = useState(false);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const [approvalModes, setApprovalModes] = useState<ApprovalMode[]>([]);
  const [dynamicVal, setDynamicVal] = useState({
    moduleName: "",
    workflowName: "",
    approvalModeId: "",
    approverDetails: "",
    isActive: true,
    createdBy: currentUserId,
  });

  useEffect(() => {
    loadApprovalModes();
    if (location.pathname.includes("/edit-approval-workflow")) {
      setIsEdit(true);
      if (id) {
        loadWorkflowData(id);
      } else {
        navigate("/approval-workflow-master");
      }
    }
  }, []);

  const loadApprovalModes = () => {
    getApprovalModesAPI()
      .then((res: any) => setApprovalModes(res || []))
      .catch();
  };

  const loadWorkflowData = (workflowId: string) => {
    getApprovalWorkflowByIdAPI(workflowId)
      .then((res: any) => {
        if (res) {
          setDynamicVal({
            moduleName: res.moduleName || "",
            workflowName: res.workflowName || "",
            approvalModeId: res.approvalModeId || "",
            approverDetails: res.approverDetails
              ? JSON.stringify(res.approverDetails, null, 2)
              : "",
            isActive: res.isActive ?? true,
            createdBy: res.createdBy || currentUserId,
          });
        }
      })
      .catch(() => navigate("/approval-workflow-master"));
  };

  const { handleChange, values, handleSubmit, touched, errors, setFieldValue } =
    useFormik({
      initialValues: dynamicVal,
      validationSchema: Yup.object({
        moduleName: Yup.string().required("Module Name is required"),
        workflowName: Yup.string().required("Workflow Name is required"),
      }),
      onSubmit: async (
        values: any,
        { setSubmitting }: FormikHelpers<any>
      ) => {
        try {
          const payload = {
            ...values,
            approverDetails: values.approverDetails
              ? JSON.parse(values.approverDetails)
              : null,
          };
          if (isEdit) {
            await editApprovalWorkflowAPI({ ...payload, id });
          } else {
            await createApprovalWorkflowAPI(payload);
          }
          navigate("/approval-workflow-master");
        } catch (err) { }
        setSubmitting(false);
      },
      enableReinitialize: true,
    });

  const isPermitted = useModulePermissions(PermissionModule.APPROVAL_WORKFLOW);
  const isAddPermitted = isPermitted(
    PermissionIdentifier.CREATE_APPROVAL_WORKFLOW
  );
  const isEditPermitted = isPermitted(
    PermissionIdentifier.EDIT_APPROVAL_WORKFLOW
  );

  const isLoading =
    createApprovalWorkflowLoading ||
    editApprovalWorkflowLoading ||
    getApprovalWorkflowByIdLoading ||
    getApprovalModesLoading;

  return (
    <div className="add-approval-workflow-wrapper">
      {isLoading && <Loading />}
      <div className="page-title">
        <h2>{isEdit ? "Edit" : "Add"} Approval Workflow</h2>
      </div>
      <div className="add-approval-workflow-box">
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label>
              Module Name<sup>*</sup>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Module Name"
              id="moduleName"
              name="moduleName"
              onChange={handleChange}
              value={values.moduleName}
              isInvalid={touched.moduleName && !!errors.moduleName}
            />
            {touched.moduleName && errors.moduleName ? (
              <Form.Control.Feedback type="invalid">
                {errors.moduleName}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>
              Workflow Name<sup>*</sup>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Workflow Name"
              id="workflowName"
              name="workflowName"
              onChange={handleChange}
              value={values.workflowName}
              isInvalid={touched.workflowName && !!errors.workflowName}
            />
            {touched.workflowName && errors.workflowName ? (
              <Form.Control.Feedback type="invalid">
                {errors.workflowName}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>Approval Mode</Form.Label>
            <Form.Select
              id="approvalModeId"
              name="approvalModeId"
              onChange={handleChange}
              value={values.approvalModeId}
            >
              <option value="">Select Approval Mode</option>
              {approvalModes.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.mode}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-4 full-width">
            <Form.Label>Approver Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter Approver Details (JSON)"
              id="approverDetails"
              name="approverDetails"
              onChange={handleChange}
              value={values.approverDetails}
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <div className="item-checkbox-content">
              <Form.Check
                inline
                label="Active"
                type="checkbox"
                id="isActive"
                name="isActive"
                onChange={handleChange}
                checked={values.isActive}
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-4 full-width text-end">
            <Button
              style={{ marginRight: "10px" }}
              variant="outline-primary"
              onClick={() => navigate("/approval-workflow-master")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
            // disabled={!isAddPermitted && !isEditPermitted}
            >
              {isEdit ? "Update" : "Add"}
            </Button>
          </Form.Group>
        </Form>
      </div>
    </div>
  );
};

export default AddApprovalWorkflow;
